import {
  staticNoCostEmiData,
  staticCheckoutCarousels,
  staticIconicCarousels
} from '@constants/index';
import { subscriptionTypes } from '../constants';

const initialState = {
  pricings: {},
  discountedPricings: {},
  discountedSubscription: {},
  referralError: null,
  paymentMethods: null,
  savedCards: null,
  emiPlans: null,
  order: null,
  paymentOngoing: false,
  paymentSuccess: false,
  paymentFailed: false,
  paymentError: null,
  purchaseSubscriptionResponse: null,
  paymentStatusResponse: null,
  purchasedSubscription: {},
  orderId: null,
  externalAppData: null,
  partPaymentStatus: null,
  partPaymentsInfo: null,
  partPaymentsFaq: null,
  showPartPaymentNudge: true,
  externalAppActivatedData: null,
  bundleDetails: {},
  comboFeatures: null,
  upiSuffixes: [],
  emiValidityChecking: false
};

const reducerMap = {
  [`${subscriptionTypes.FETCH_UPI_SUFFIX_LIST}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      upiSuffixes: action.result
    };
  },
  [`${subscriptionTypes.FETCH_ELIGIBLE_GOALS}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      loan_eligible_goals: action.result?.eligible_goals,
      loan_gateways: action.result?.loan_gateways
    };
  },
  [`${subscriptionTypes.VERIFY_DC_EMI_ELIGIBILITY}_REQUEST`]: (state) => {
    return {
      ...state,
      emiValidityChecking: true
    };
  },
  [`${subscriptionTypes.VERIFY_DC_EMI_ELIGIBILITY}_SUCCESS`]: (state) => {
    return {
      ...state,
      emiValidityChecking: false
    };
  },
  [`${subscriptionTypes.VERIFY_DC_EMI_ELIGIBILITY}_FAILURE`]: (state) => {
    return {
      ...state,
      emiValidityChecking: false
    };
  },
  [`${subscriptionTypes.FETCH_CARD_BANK_ALIASES}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      bankAliases: action.result
    };
  },
  [`${subscriptionTypes.FETCH_CASH_PAYMENT_DATA}_SUCCESS`]: (state, action) => {
    const clashDetails =
      action.result.message && action.result.clash_details
        ? { clash_details: action.result.clash_details }
        : null;
    return {
      ...state,
      cashResponse: action.result.message
        ? clashDetails
        : {
            ...action.result,
            subscription_details: {
              ...action.result.subscription_details,
              value: action.result.goal
            }
          }
    };
  },
  [`${subscriptionTypes.CANCEL_CASH_PAYMENT}_SUCCESS`]: (state) => {
    return {
      ...state,
      cashResponse: null
    };
  },
  [`${subscriptionTypes.RESET_CASH_RESPONSE}`]: (state) => {
    return {
      ...state,
      cashResponse: null
    };
  },
  [`${subscriptionTypes.ENROLL_ORDER}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      order: action.result,
      cashResponse: action.isCashPayment
        ? {
            ...action.result,
            subscription_details: {
              ...action.result.subscription_details,
              value: action.result.goal
            }
          }
        : null
    };
  },
  [`${subscriptionTypes.ENROLL_INSTALLMENT_ORDER}_SUCCESS`]: (
    state,
    action
  ) => {
    return {
      ...state,
      order: action.result
    };
  }
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case `${subscriptionTypes.FETCH_PRICINGS}_SUCCESS`:
      return {
        ...state,
        pricings: {
          ...state.pricings,
          [action.uid]: action.result
        }
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL}_REQUEST`:
      return {
        ...state,
        referralError: null
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL}_SUCCESS`:
      return {
        ...state,
        referralError: null,
        discountedPricings: {
          ...state.discountedPricings,
          [action.uid]: action.result
        }
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL}_FAILURE`:
      return {
        ...state,
        referralError: action.error,
        discountedPricings: {
          ...state.discountedPricings,
          [action.uid]: null
        }
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL_WITH_SUBSCRIPTION_ID}_REQUEST`: {
      return {
        ...state,
        referralError: null
      };
    }

    case `${subscriptionTypes.VALIDATE_REFERRAL_WITH_SUBSCRIPTION_ID}_SUCCESS`: {
      const subscriptionIdData = action.result.find(
        (subscription) =>
          subscription.subscription?.uid === action.subscriptionId
      );
      return {
        ...state,
        referralError: null,
        discountedSubscription: {
          ...state.discountedSubscription,
          [action.uid]: subscriptionIdData
        }
      };
    }

    case `${subscriptionTypes.VALIDATE_REFERRAL_WITH_SUBSCRIPTION_ID}_FAILURE`:
      return {
        ...state,
        referralError: action.error,
        discountedSubscription: {
          ...state.discountedSubscription,
          [action.uid]: null
        }
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL_SUBSCRIPTION}_REQUEST`:
      return {
        ...state,
        referralError: null
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL_SUBSCRIPTION}_SUCCESS`:
      return {
        ...state,
        referralError: null,
        discountedSubscription: {
          ...state.discountedSubscription,
          [action.uid]: action.result
        }
      };

    case `${subscriptionTypes.VALIDATE_REFERRAL_SUBSCRIPTION}_FAILURE`:
      return {
        ...state,
        referralError: action.error,
        discountedSubscription: {
          ...state.discountedSubscription,
          [action.uid]: null
        }
      };

    case `${subscriptionTypes.FETCH_ALL_PAYMENT_METHODS}_SUCCESS`:
      return {
        ...state,
        paymentMethods: {
          cash: action.result.cash,
          ...action.result.juspay
        }
      };

    case `${subscriptionTypes.FETCH_SAVED_CARDS}_SUCCESS`:
      return {
        ...state,
        savedCards: action.result
      };

    case `${subscriptionTypes.FETCH_EMI_PLANS}_SUCCESS`:
      return {
        ...state,
        emiPlans: action.result
      };

    case `${subscriptionTypes.FETCH_PART_PAYMENT_STATUS}_SUCCESS`:
      return {
        ...state,
        partPaymentStatus: {
          ...state.partPaymentStatus,
          [action.key]: action.result
        }
      };

    case subscriptionTypes.CLEAR_REFERRAL_ERROR:
      return {
        ...state,
        referralError: null
      };

    case subscriptionTypes.CLEAR_REFERRAL_PRICINGS:
      return {
        ...state,
        discountedPricings: {
          ...state.discountedPricings,
          [action.uid]: null
        }
      };

    case subscriptionTypes.CLEAR_REFERRAL_SUBSCRIPTION:
      return {
        ...state,
        discountedSubscription: {
          ...state.discountedSubscription,
          [action.uid]: null
        }
      };

    case `${subscriptionTypes.PURCHASE_SUBSCRIPTION}_SUCCESS`:
      return {
        ...state,
        purchaseSubscriptionResponse: action.result
      };

    case `${subscriptionTypes.PAYMENT_STATUS_JUSPAY}_SUCCESS`:
      return {
        ...state,
        paymentStatusResponse: action.result
      };

    case subscriptionTypes.SAVE_PURCHASED_SUBSCRIPTION:
      return {
        ...state,
        purchasedSubscription: {
          ...state.purchasedSubscription,
          [action.goalOrComboId]: action.purchasedSubscription
        },
        orderId: action.orderId
      };

    case subscriptionTypes.CLEAR_ORDER:
      return {
        ...state,
        order: null
      };

    case `${subscriptionTypes.ACTIVATE_EXTERNAL_APP}_SUCCESS`:
      return {
        ...state,
        externalAppData: action.result
      };

    case `${subscriptionTypes.FETCH_PART_PAYMENTS_INFOS}_SUCCESS`:
      return {
        ...state,
        partPaymentsInfo: action.result
      };

    case `${subscriptionTypes.FETCH_PART_PAYMENTS_FAQS}_SUCCESS`:
      return {
        ...state,
        partPaymentsFaq: action.result
      };

    case `${subscriptionTypes.FETCH_BUNDLE_DETAILS}_SUCCESS`:
      return {
        ...state,
        bundleDetails: {
          ...state.bundleDetails,
          [action.uid]: action.result
        }
      };

    case `${subscriptionTypes.STOP_PART_PAYMENT_NUDGE}_SUCCESS`:
      return {
        ...state
      };

    case subscriptionTypes.HIDE_PART_PAYMENT_NUDGE:
      return {
        ...state,
        showPartPaymentNudge: false
      };

    case `${subscriptionTypes.SYNC_CODE_CHEF}_SUCCESS`:
      return {
        ...state,
        externalAppActivatedData: {
          ...action.result,
          user_subscription: {
            ...action.result.subscription,
            external_app_subscriptions: [
              {
                external_app: action.result.external_app
              }
            ]
          }
        }
      };

    case `${subscriptionTypes.FETCH_SUBSCRIPTION_STATIC_DATA}_SUCCESS`:
      return {
        ...state,
        staticContent: {
          [action.key]: action.result
        }
      };

    case `${subscriptionTypes.FETCH_SUBSCRIPTION_STATIC_DATA}_FAILURE`:
      return {
        ...state,
        staticContent: {
          [action.key]: {
            carousels: staticCheckoutCarousels,
            iconicCarousels: staticIconicCarousels,
            noCostEmi: staticNoCostEmiData
          }
        }
      };

    case 'SET_EXTERNAL_SUBSCRIPTION_DATA':
      return {
        ...state,
        purchaseSubscriptionResponse: {
          ...state.purchaseSubscriptionResponse,
          user_subscription: {
            external_app_subscriptions: [action.subscriptionData]
          }
        }
      };

    case `${subscriptionTypes.FETCH_COMBO_FEATURE_POINTS}_SUCCESS`:
      return {
        ...state,
        comboFeatures: action.result
      };

    default: {
      const actionHandler = reducerMap[action.type];
      return actionHandler ? actionHandler(state, action) : state;
    }
  }
};

export default reducer;
